<template>
  <div class="customer-contact-manager" :class="{ embedded }">
    <el-alert
      v-if="commercialError"
      :closable="false"
      type="error"
      class="page-alert"
      show-icon
    >
      {{ commercialError }}
    </el-alert>

    <el-card :shadow="embedded ? 'never' : 'always'" v-loading="loading">
      <div slot="header" class="card-header">
        <div>
          <div class="card-title">{{ embedded ? '客服方式' : '平台账号列表' }}</div>
          <div class="card-header-tip">
            每个平台只保留一条记录，聊天平台会直接打开对话，社媒平台会跳转到账号主页。
          </div>
        </div>
        <div class="card-actions">
          <el-button size="mini" icon="el-icon-refresh" @click="initialize">刷新</el-button>
          <el-button
            size="mini"
            type="primary"
            icon="el-icon-plus"
            :disabled="allPlatformsUsed"
            @click="openCreateDialog"
          >
            新增
          </el-button>
        </div>
      </div>

      <el-table :data="accounts" border stripe empty-text="暂无客服平台账号">
        <el-table-column prop="platform" label="平台" min-width="140">
          <template slot-scope="{ row }">
            {{ platformLabel(row.platform) }}
          </template>
        </el-table-column>

        <el-table-column prop="account" label="账号" min-width="220" show-overflow-tooltip />

        <el-table-column label="跳转预览" min-width="320" show-overflow-tooltip>
          <template slot-scope="{ row }">
            {{ previewUrl(row.platform, row.account) || '-' }}
          </template>
        </el-table-column>

        <el-table-column prop="status" label="状态" width="120" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="Number(row.status) === 1 ? 'success' : 'info'">
              {{ Number(row.status) === 1 ? '启用' : '停用' }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="操作" width="220" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" @click="handlePreview(row)">预览</el-button>
            <el-button type="text" @click="openEditDialog(row)">编辑</el-button>
            <el-button
              type="text"
              class="danger-text"
              :loading="deletingId === row.id"
              @click="handleDelete(row)"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-dialog
      :title="dialogMode === 'create' ? '新增平台账号' : '编辑平台账号'"
      :visible.sync="dialogVisible"
      width="560px"
      @closed="resetForm"
    >
      <el-form ref="formRef" :model="form" :rules="rules" label-width="96px">
        <el-form-item label="平台" prop="platform">
          <el-select v-model="form.platform" placeholder="请选择平台" style="width: 100%">
            <el-option
              v-for="option in platformOptions"
              :key="option.value"
              :label="option.label"
              :value="option.value"
              :disabled="isPlatformDisabled(option.value)"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="账号" prop="account">
          <div class="account-input-wrap">
            <el-input
              v-model.trim="form.account"
              placeholder="请输入账号名、手机号或 handle"
              clearable
            />
            <el-tooltip placement="top" effect="dark">
              <div slot="content" class="hint-tooltip">
                <div class="hint-title">{{ activeHint.title }}</div>
                <div>{{ activeHint.format }}</div>
                <div>示例账号：{{ activeHint.exampleInput }}</div>
                <div>示例链接：{{ activeHint.exampleUrl }}</div>
              </div>
              <i class="el-icon-question hint-icon"></i>
            </el-tooltip>
          </div>
        </el-form-item>

        <el-form-item label="预览地址">
          <div class="preview-text">{{ previewUrl(form.platform, form.account) || '-' }}</div>
        </el-form-item>

        <el-form-item label="状态" prop="status">
          <el-switch
            v-model="form.status"
            :active-value="1"
            :inactive-value="0"
            active-text="启用"
            inactive-text="停用"
          />
        </el-form-item>
      </el-form>

      <div slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submitForm">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  addCustomerAccount,
  deleteCustomerAccount,
  getUserInfo,
  listCustomerAccounts,
  updateCustomerAccount,
} from '@/api'
import {
  buildCustomerContactUrl,
  getCustomerAccountHint,
  normalizeCustomerAccount,
  normalizeCustomerPlatform,
} from '@/utils/customer-contact'

const PLATFORM_OPTIONS = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'messenger', label: 'Messenger' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'ins', label: 'Instagram' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'pinterest', label: 'Pinterest' },
  { value: 'telegram', label: 'telegram' },
  { value: 'line', label: 'line' },
]

const DEFAULT_FORM = () => ({
  id: null,
  platform: 'whatsapp',
  account: '',
  status: 1,
  config: '',
  remark: '',
  nickname: '',
})

export default {
  name: 'CustomerContactManager',
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '客服联系方式',
    },
    description: {
      type: String,
      default: '这里保存的是平台账号，而不是完整链接。',
    },
    showBackButton: {
      type: Boolean,
      default: false,
    },
    backTo: {
      type: String,
      default: '/',
    },
  },
  data() {
    const validateAccount = (rule, value, callback) => {
      if (!normalizeCustomerAccount(value)) {
        callback(new Error('请输入账号'))
        return
      }
      callback()
    }

    const validatePlatform = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请选择平台'))
        return
      }

      if (this.isPlatformDisabled(value)) {
        callback(new Error('该平台已存在记录'))
        return
      }

      callback()
    }

    return {
      loading: false,
      saving: false,
      deletingId: null,
      dialogVisible: false,
      dialogMode: 'create',
      commercialId: null,
      commercialError: '',
      accounts: [],
      form: DEFAULT_FORM(),
      platformOptions: PLATFORM_OPTIONS,
      rules: {
        platform: [{ validator: validatePlatform, trigger: 'change' }],
        account: [{ validator: validateAccount, trigger: 'blur' }],
      },
    }
  },
  computed: {
    allPlatformsUsed() {
      return this.accounts.length >= this.platformOptions.length
    },
    activeHint() {
      return getCustomerAccountHint(this.form.platform)
    },
  },
  mounted() {
    this.initialize()
  },
  methods: {
    previewUrl(platform, account) {
      return buildCustomerContactUrl(platform, account)
    },
    async initialize() {
      this.loading = true
      this.commercialError = ''

      try {
        const { data } = await getUserInfo()
        this.commercialId = data?.commercial_id || data?.commercial?.id || null

        if (!this.commercialId) {
          this.accounts = []
          this.commercialError = '当前用户信息缺少 commercial_id，无法保存客服联系方式。'
          return
        }

        await this.fetchAccounts()
        this.$emit('loaded', this.accounts.slice())
      } catch (error) {
        this.accounts = []
        this.commercialError = error.message || '加载客服联系方式失败'
        this.$emit('error', this.commercialError)
      } finally {
        this.loading = false
      }
    },
    async fetchAccounts() {
      const { data } = await listCustomerAccounts()
      const rows = this.extractList(data)

      this.accounts = rows.map(item => ({
        id: item.id,
        platform: normalizeCustomerPlatform(item.platform),
        account: item.account || '',
        status: Number(item.status) === 1 ? 1 : 0,
        config: item.config || '',
        remark: item.remark || '',
        nickname: item.nickname || '',
      }))
    },
    extractList(payload) {
      if (Array.isArray(payload)) return payload
      if (Array.isArray(payload?.data)) return payload.data
      if (Array.isArray(payload?.list)) return payload.list
      if (Array.isArray(payload?.items)) return payload.items
      if (Array.isArray(payload?.rows)) return payload.rows
      if (Array.isArray(payload?.data?.data)) return payload.data.data
      return []
    },
    platformLabel(value) {
      const normalized = normalizeCustomerPlatform(value)
      const matched = this.platformOptions.find(item => item.value === normalized)
      return matched ? matched.label : value || '-'
    },
    isPlatformDisabled(platform) {
      const normalized = normalizeCustomerPlatform(platform)
      return this.accounts.some(item => normalizeCustomerPlatform(item.platform) === normalized && item.id !== this.form.id)
    },
    getFirstAvailablePlatform() {
      const available = this.platformOptions.find(option => !this.isPlatformDisabled(option.value))
      return available ? available.value : this.platformOptions[0].value
    },
    openCreateDialog() {
      this.dialogMode = 'create'
      this.form = {
        ...DEFAULT_FORM(),
        platform: this.getFirstAvailablePlatform(),
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.formRef) this.$refs.formRef.clearValidate()
      })
    },
    openEditDialog(row) {
      this.dialogMode = 'edit'
      this.form = {
        id: row.id,
        platform: normalizeCustomerPlatform(row.platform),
        account: row.account,
        status: Number(row.status) === 1 ? 1 : 0,
        config: row.config || '',
        remark: row.remark || '',
        nickname: row.nickname || '',
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.formRef) this.$refs.formRef.clearValidate()
      })
    },
    resetForm() {
      this.form = DEFAULT_FORM()
      if (this.$refs.formRef) {
        this.$refs.formRef.resetFields()
      }
    },
    handlePreview(row) {
      const url = this.previewUrl(row.platform, row.account)
      if (!url) {
        this.$message.warning('无法生成预览链接')
        return
      }
      window.open(url, '_blank')
    },
    submitForm() {
      if (!this.commercialId) {
        this.$message.error('缺少 commercial_id，无法保存')
        return
      }

      this.$refs.formRef.validate(async valid => {
        if (!valid) return

        this.saving = true

        try {
          const payload = {
            platform: this.form.platform,
            account: normalizeCustomerAccount(this.form.account),
            status: this.form.status,
            config: this.form.config || '',
            remark: this.form.remark || '',
            nickname: this.form.nickname || '',
          }

          if (this.dialogMode === 'create') {
            await addCustomerAccount({
              commercial_id: this.commercialId,
              ...payload,
            })
          } else {
            await updateCustomerAccount({
              id: this.form.id,
              ...payload,
            })
          }

          this.$message.success(this.dialogMode === 'create' ? '新增成功' : '保存成功')
          this.dialogVisible = false
          await this.fetchAccounts()
          this.$emit('loaded', this.accounts.slice())
        } catch (error) {
          this.$message.error(error.message || '保存失败')
        } finally {
          this.saving = false
        }
      })
    },
    async handleDelete(row) {
      try {
        await this.$confirm(`确认删除 ${this.platformLabel(row.platform)} 账号吗？`, '提示', {
          type: 'warning',
        })
      } catch (_) {
        return
      }

      this.deletingId = row.id

      try {
        await deleteCustomerAccount(row.id)
        this.$message.success('删除成功')
        await this.fetchAccounts()
        this.$emit('loaded', this.accounts.slice())
      } catch (error) {
        this.$message.error(error.message || '删除失败')
      } finally {
        this.deletingId = null
      }
    },
  },
}
</script>

<style scoped>
.customer-contact-manager.embedded {
  min-height: auto;
}

.page-head {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  align-items: flex-start;
  margin-bottom: 20px;
}

.page-head h1 {
  margin: 0 0 8px;
  font-size: 28px;
  color: #1f2937;
}

.page-head p {
  margin: 0;
  color: #5b6472;
  line-height: 1.6;
}

.page-actions,
.card-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.page-alert {
  margin-bottom: 16px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.card-title {
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
}

.card-header-tip {
  margin-top: 6px;
  color: #909399;
  font-size: 13px;
  line-height: 1.6;
}

.account-input-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
}

.account-input-wrap .el-input {
  flex: 1;
}

.hint-icon {
  font-size: 18px;
  color: #409eff;
  cursor: pointer;
}

.hint-tooltip {
  line-height: 1.7;
  max-width: 360px;
}

.hint-title {
  font-weight: 700;
}

.preview-text {
  color: #606266;
  word-break: break-all;
  line-height: 1.6;
}

.danger-text {
  color: #f56c6c;
}

@media (max-width: 768px) {
  .page-head,
  .card-header {
    flex-direction: column;
  }

  .page-actions,
  .card-actions {
    width: 100%;
  }

  .account-input-wrap {
    align-items: flex-start;
  }
}
</style>
